// useInterval.js
'use strict';
import { useEffect, useRef } from 'react';

import { NULL_FUNCTION } from '../resource/defaults.js';

const DEFAULT_INTERVAL_SECONDS = 10;

/**
 * Interval hook
 * - Supports canExecute / execute / intervalSeconds
 * - Stop interval permanently when execute throws
 */
const useInterval = (options = {}) => {
  const {
    canExecute = false,
    execute = NULL_FUNCTION,
    intervalSeconds = DEFAULT_INTERVAL_SECONDS,
  } = options || {};

  const inIntervalRef = useRef(false);
  const executeRef = useRef(execute);

  // Avoid restarting the interval timer on every render (execute identity changes),
  // but still always call the latest execute.
  useEffect(() => {
    executeRef.current =
      typeof execute === 'function' ? execute : NULL_FUNCTION;
  }, [execute]);

  useEffect(() => {
    if (!canExecute) return;

    const isIntervalValid =
      Number.isFinite(intervalSeconds) && intervalSeconds > 0;
    const validIntervalSeconds = isIntervalValid
      ? intervalSeconds
      : DEFAULT_INTERVAL_SECONDS;

    let stoppedByError = false;
    let timer = null;
    let cancelled = false; // to avoid scheduling after unmount

    const stop = () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const runOnce = async () => {
      if (cancelled || stoppedByError) return;
      if (inIntervalRef.current) return;

      inIntervalRef.current = true;
      try {
        await executeRef.current();
      } catch (_) {
        stoppedByError = true;
        stop();
        return;
      } finally {
        inIntervalRef.current = false;
      }

      if (cancelled || stoppedByError) return;
      timer = setTimeout(runOnce, validIntervalSeconds * 1000);
    };

    // Always execute immediately
    runOnce();

    return () => stop();
  }, [canExecute, intervalSeconds]);
};

export default useInterval;
